import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useOverlayShortcut } from '../hooks/useOverlayShortcut';
import { Box, Button, Flex, Text } from 'rebass/styled-components';
import { InputNoForm } from './TextInput';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { SEARCH_PATIENT_BY_NAME } from '../graphql/queries';
import { ISearchProviderPatientsByNameOutput, ISearchProviderPatientsByNameInput } from '../graphql/types/queries/patient';
import { HighlightedText } from './HiglightText';
import { isLocalMode, isPreviewMode } from '../utils/environment';
import { toast } from 'react-toastify';
import { GENERATE_WEBTOKEN_FOR_IMPERSONATE } from '../graphql/mutations';
import { IGenerateWebTokenFromHCOutput, IGenerateWebTokenFromHCInput } from '../graphql/types/mutations/login';
import Modal from 'styled-react-modal';
import SendSms from './SendSms';
import { formatPhoneNumber } from '../utils/formatPhoneNumber';
import { RequestReplacementKit } from '../pages/patient/components/RequestReplacementKit';
import theme from '../styles/theme';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(159, 159, 159, 0.45);       // semi-transparent white
  backdrop-filter: blur(3px);               // blur the background behind it
  -webkit-backdrop-filter: blur(10px);       // Safari support
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3); // subtle border
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);  // soft shadow for depth
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled(Flex)`
  background: white;
  padding: 16px;
  border-radius: 8px;
  min-width: min(1050px, 75vw);
  max-width: min(1050px, 75vw);
  width: min(1050px, 75vw) ;
  flex-direction: column;
  max-height:80vh ;
  overflow:hidden;
`;

const CloseButtonWrapper = styled.button`
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    stroke: ${({ theme }) => theme.color.primary || '#333'};
    stroke-width: 2;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 50%;
  }

  &:focus {
    outline: none;
  }
`;

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const Separator = styled(Box)`
  width: 100%;
  height:1px ;
  background-color: #ededed;
  margin-top: 5px ;
  margin-bottom: 5px;
`

const SeparatorVertical = styled(Box)`
  width: 1px;
  height:100% ;
  background-color: #ededed;
  margin-top: 5px ;
  margin-bottom: 5px;
`

const SEARCH_PATIENT = gql`
  query searchProviderPatientsExtensive($search: String!) {
    searchProviderPatientsExtensive(search: $search) {
      id
      email
      phone
      firstName
      lastName
      intakeStatus
      athenaId
      helpscoutId
      stripeId
      createdAt
      accountLocked
      mustCompleteProfile
      profileCompletionUid
      intakeDate
      isZocDoc
      careManager
      mustCompleteBaselineSurvey
      mustScheduleOnboardingCall
      perpetuousPhysicianConversationId
      perpetuousMemberConversationId
      isHealthCoachingEnabled
      healthCoachingStatus
      menoIntakeStatus
      menoIntakeDate
      weightLossIntakeStatus
      weightLossIntakeDate
      bridgePatientId
      forceBypassBridge
      isSuggestion
    }
  }
`;
interface ISearchProviderPatients {
  id: string
  email: string
  phone: string
  firstName: string
  lastName: string
  intakeStatus?: string
  athenaId?: string
  helpscoutId?: string
  stripeId?: string
  createdAt: string
  accountLocked?: boolean
  mustCompleteProfile?: boolean
  profileCompletionUid?: string
  intakeDate?: string
  isZocDoc?: boolean
  careManager?: string
  mustCompleteBaselineSurvey?: boolean
  mustScheduleOnboardingCall?: boolean
  perpetuousPhysicianConversationId?: string
  perpetuousMemberConversationId?: string
  isHealthCoachingEnabled?: string
  healthCoachingStatus?: string
  menoIntakeStatus?: string
  menoIntakeDate?: string
  weightLossIntakeStatus?: string
  weightLossIntakeDate?: string
  bridgePatientId?: string
  forceBypassBridge?: boolean
  isSuggestion: boolean
}
interface ISearchProviderPatientsOutput {
  searchProviderPatientsExtensive: ISearchProviderPatients[]
}
interface ISearchProviderPatientsInput {
  search: string
}

const ResultCardWrapper = styled(Flex) <{
  isSelected?: boolean
}>`
    border: 1px solid transparent;
  background:transparent ;
  padding: 6px 8px ;
  cursor:pointer;
  flex-direction: column;
  border-radius: 10px ;
  margin-bottom: 10px ;
  &:hover {
    border: 1px solid #344f7955;
    ${({ isSelected }) => isSelected && `
      border: 1px solid #344f79;
    `}
  }
  ${({ isSelected }) => isSelected && `
    border: 1px solid #344f79;
  `}
`

const ResultCard: FC<{ patient: ISearchProviderPatients, isSelected?: boolean, search: string, onClick: (patient: ISearchProviderPatients) => void }> = ({ patient, isSelected, onClick, search }) => {
  return (
    <ResultCardWrapper isSelected={isSelected} onClick={() => onClick(patient)}>
      <Text>{patient.firstName} {patient.lastName}  -  <HighlightedText match={search} text={`${patient.athenaId}`}></HighlightedText></Text>
      <HighlightedText match={search} fontSize={'13px'} text={`${patient.email}`}></HighlightedText>
    </ResultCardWrapper>
  )
}

const NavigateOverlayBox = () => {
  const toastId = useRef<any>(null);
  const [isSendSms, setIsSendSms] = useState(false)
  const [isSendReplacementKit, setIsSendReplacementKit] = useState(false)
  const [search, setSearch] = useState<string>()
  const [selectedPatient, setSelectedPatient] = useState<ISearchProviderPatients | undefined>()
  const [prefix, setPrefix] = useState<'' | null | 'name / email' | 'athenaId' | 'email' | 'full name' | 'navigate to'>(null)
  const [isOpen, setIsOpen] = useState(false);
  const [result, setResult] = useState<ISearchProviderPatients[] | null | undefined>()
  const [hasTriggeredSearch, setHasTriggeredSearch] = useState<boolean | null>(false)
  const [searchPatients, { data: dataSearch, loading: loadingSearch, error: errorSearch }] = useLazyQuery<ISearchProviderPatientsOutput, ISearchProviderPatientsInput>(SEARCH_PATIENT, {
    fetchPolicy: 'network-only',
    onCompleted: e => {
      console.log(e)
      setResult(e.searchProviderPatientsExtensive)
      if (e.searchProviderPatientsExtensive && e.searchProviderPatientsExtensive.length > 0 && e.searchProviderPatientsExtensive.filter(x => !x.isSuggestion)) {
        setSelectedPatient(e.searchProviderPatientsExtensive.filter(x => !x.isSuggestion)[0])
      }
    }
  })
  const [impersonate] = useMutation<IGenerateWebTokenFromHCOutput, IGenerateWebTokenFromHCInput>(GENERATE_WEBTOKEN_FOR_IMPERSONATE, {
    onCompleted: (data) => {
      if (!data || !data.generateWebTokenFromHC || !data.generateWebTokenFromHC.token) {
        toast.update(toastId.current, {
          render: (<Flex flexDirection={'column'}>
            <Text fontWeight={400}>An error occured</Text>
          </Flex>),
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
        return
      }
      if (toastId && toastId.current) {
        toast.update(toastId.current, {
          render: (<Flex flexDirection={'column'}>
            <Text fontWeight={400}>Impersonation prerequisite successful</Text>
          </Flex>),
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      } else {
        toast.success(<Flex flexDirection={'column'}>
          <Text fontWeight={400}>Impersonation prerequisite successful</Text>
        </Flex>, {
          autoClose: 5000
        })
      }

      if (!selectedPatient) return
      window.open(`${(window as any)._env_.REACT_APP_WEBAPP_URL}/hc/autosign/sso/${data.generateWebTokenFromHC.token}/${selectedPatient!.athenaId}`, '_blank')
    },
    onError: () => {
      if (toastId && toastId.current) {
        toast.update(toastId.current, {
          render: (<Flex flexDirection={'column'}>
            <Text fontWeight={400}>An error occured</Text>
          </Flex>),
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
      } else {
        toast.error(<Flex flexDirection={'column'}>
          <Text fontWeight={400}>An error occured</Text>
        </Flex>, {
          autoClose: 5000
        })
      }
    }
  })
  const navigate = useNavigate()

  // Trigger overlay when Ctrl + F is pressed
  useOverlayShortcut(() => setIsOpen(true));

  const closeOverlay = () => setIsOpen(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeOverlay();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  useEffect(() => {
    if (!search) {
      setPrefix(null)
      return
    }

    let value: string | number

    try {
      value = parseInt(search)
    } catch (error) {
      value = search
    }

    if (!value) {
      value = search
    }

    if (isNaN(value as any)) {
      if (value.toString().indexOf(' ') > -1) {
        setPrefix('full name')
        return
      }
      if (value.toString().indexOf('@') > -1 || value.toString().indexOf('.') > -1 || value.toString().indexOf('+') > -1) {
        setPrefix('email')
        return
      }
      setPrefix('name / email')
      return
    } else {
      setPrefix('athenaId')
    }
  }, [search])

  if (!isOpen) return null;
  const onSearch = () => {
    if(!!loadingSearch) return
    setResult(undefined)
    if (!search) return
    // if (prefix === 'athenaId') {
    //   navigate(`/patient/${search.trim()}`)
    //   return
    // }
    setHasTriggeredSearch(true)
    searchPatients({
      variables: {
        search
      }
    })
  }
  const enterSearch = (text: string) => {
    setResult(undefined)
    setSearch(text)
    setHasTriggeredSearch(false)
    setSelectedPatient(undefined)
    setIsSendSms(false)
    setIsSendReplacementKit(false)
    // setHasTriggeredSearch(false)
  }
  let webappRoute = 'https://app.palomahealth.com'
  if (isPreviewMode) {
    webappRoute = !isLocalMode ? 'https://preview-app.paloma.network' : 'http://localhost:3000'
  }

  const prefixStyle = (pref: typeof prefix) => {
    if (prefix === 'athenaId') {
      return {
        background: '#FFE6E2',
        color: '#F6A293',
        text: 'Athena ID:'
      }
    }
    if (prefix === 'email') {
      return {
        background: '#e2f4ff',
        color: '#466b93',
        text: 'Email:'
      }
    }
    if (prefix === 'full name' || prefix === 'name / email') {
      return {
        background: '#aeeedb',
        color: '#46937c',
        text: prefix
      }
    }
    return {
      background: '#FFE6E2',
      color: '#F6A293',
      text: ''
    }
  }
  return (
    <Overlay onClick={closeOverlay}>
      <Container onClick={(e) => e.stopPropagation()} style={{ height: !!result ? '80vh' : '' }}>
        <Flex justifyContent={'space-between'} alignItems={'center'}>
          <Text>Quick Search</Text>
          <CloseButtonWrapper onClick={closeOverlay}>
            <CloseIcon />
          </CloseButtonWrapper>
        </Flex>
        <Separator />
        <Flex flexDirection={'row'} style={{ background: 'white', borderRadius: '12px', padding: '0 12px', height: '32px', overflow: "hidden" }} alignItems='center'
          onClick={() => {
          }}>
          {!!prefix ? (
            <Box style={{ background: prefixStyle(prefix).background, height: '100%', margin: '0 0 0 -12px', display: 'flex', alignItems: 'center', padding: '0 8px 0 12px', marginRight: '12px' }}>
              <Text color={prefixStyle(prefix).color}>{prefixStyle(prefix).text}</Text>
            </Box>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" >
              <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 12.7543 18.1045 14.9163 16.5104 16.5104C14.9163 18.1045 12.7543 19 10.5 19C8.24566 19 6.08365 18.1045 4.48959 16.5104C2.89553 14.9163 2 12.7543 2 10.5C2 8.24566 2.89553 6.08365 4.48959 4.48959C6.08365 2.89553 8.24566 2 10.5 2C12.7543 2 14.9163 2.89553 16.5104 4.48959C18.1045 6.08365 19 8.24566 19 10.5V10.5Z" stroke="#344F7A" stroke-width="2" stroke-linecap="round" />
            </svg>
          )}
          <InputNoForm type="search"
            onReturn={onSearch}
            autoFocus
            onChange={(e: any) => {
              enterSearch(e.target.value)
            }}
            style={{ backgroundColor: 'white', border: '0px', flex: 1, paddingTop: '12px', paddingBottom: '12px' }}
            value={search}
            containerStyle={{ flex: 1 }}
            placeholder='' />

        </Flex>
        <Separator />
        {!!result && result.length < 1 && (
          <Flex justifyContent={'center'} alignItems={'center'} flexDirection={'column'} flex={1}>
            <Text fontWeight={500} style={{ opacity: 0.5 }}>No results matching {search}</Text>
            <Text mt={2} style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }} textAlign={'center'}>You can search by Athena ID, any part of a name, or an email to search.</Text>
            <Text mt={2} style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }} mb={2} textAlign={'center'}>Non-numeric searches (non athena id) require <span style={{ textDecoration: 'underline' }}>at least 3 characters</span></Text>
            <Text mt={3} style={{ fontSize: '13px', opacity: 0.5 }}>Search examples:</Text>
            <ul>
              <li><Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>Doe</Text></li>
              <li><Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>John</Text></li>
              <li><Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>John Doe</Text></li>
              <li><Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>John.doe@gmail</Text></li>
            </ul>
          </Flex>
        )}
        {!!loadingSearch && (
          <Flex justifyContent={'center'} alignItems={'center'} flex={1}>
            <Text fontWeight={500} mt={4} mb={4} style={{ opacity: 0.5 }}>Searching: {search}...</Text>
          </Flex>
        )}
        {!result && !loadingSearch && (
          <Flex justifyContent={'center'} alignItems={'center'} flex={1} mt={3} mb={3} flexDirection={'column'}>
              <Text mt={2} fontWeight={500} style={{ opacity: 0.5 }} textAlign={'center'}>Local Database Quick Search (not Athena).</Text>
              <Text mt={2} style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }} textAlign={'center'}>You can search by Athena ID, any part of a name, or an email to search.</Text>
              <Text mt={2} style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }} mb={2} textAlign={'center'}>Non-numeric searches (non athena id) require <span style={{ textDecoration: 'underline' }}>at least 3 characters</span></Text>
              <Text mt={3} style={{ fontSize: '13px', opacity: 0.5 }}>Search examples:</Text>
              <ul>
                <li><Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>2173</Text></li>
                <li><Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>Doe</Text></li>
                <li><Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>John</Text></li>
                <li><Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>John Doe</Text></li>
                <li><Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>John.doe@gmail</Text></li>
              </ul>
          </Flex>
        )}
        {!!result && result.length > 0 && (
          <>
            <Flex flex={1} style={{ overflow: 'hidden' }}>
              <Flex flexDirection={'column'} flex={1} style={{ overflow: 'auto' }}>
                <Text style={{ fontSize: '13px', fontWeight: 300, opacity: 0.8 }}>Perfect Match:</Text>
                {result.filter(x => !x.isSuggestion).length > 0 ? (
                  <>
                    {result.filter(x => !x.isSuggestion).map(x =>
                      <ResultCard search={search || ''} key={x.id} patient={x} isSelected={selectedPatient && selectedPatient.id === x.id} onClick={patient => setSelectedPatient(patient)} />
                    )}
                  </>) : (
                  <>
                    <Text mt={2} style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }} mb={2} textAlign={'center'}>No Match Found</Text>
                  </>
                )}
                {result.filter(x => x.isSuggestion).length > 0 && (
                  <>
                    <Text mt={3} style={{ fontSize: '13px', fontWeight: 300, opacity: 0.8 }}>Similar Match :</Text>
                    {result.filter(x => !!x.isSuggestion).map(x =>
                      <ResultCard search={search || ''} key={x.id} patient={x} isSelected={selectedPatient && selectedPatient.id === x.id} onClick={patient => setSelectedPatient(patient)} />
                    )}
                    {result.filter(x => !!x.isSuggestion).length > 9 && (
                      <Text mt={2} style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }} textAlign={'center'}>Search Limited to first 10 occurences</Text>
                    )}
                  </>
                )}
              </Flex>
              <SeparatorVertical ml={2} />
              {!!selectedPatient && (
                <Flex style={{ width: '30%', overflow: 'auto' }} p={2} flexDirection={'column'}>
                  <Text fontSize={'16px'} textAlign={'center'}>{selectedPatient.firstName}</Text>
                  <Text fontSize={'16px'} textAlign={'center'}>{selectedPatient.lastName}</Text>
                  <Text fontSize={'14px'} mt={2} textAlign={'center'} style={{ fontWeight: 300, opacity: 0.75 }}>{selectedPatient.email}</Text>
                  <Text fontSize={'14px'} textAlign={'center'} style={{ fontWeight: 300, opacity: 0.75 }}>{formatPhoneNumber(selectedPatient.phone)}</Text>
                  <Text fontSize={'14px'} textAlign={'center'} style={{ fontWeight: 300, opacity: 0.75 }}>Athena ID: <span style={{ fontWeight: 500, color: !selectedPatient.athenaId ? 'red' : '' }}>{(selectedPatient.athenaId || 'No Athena ID')}</span></Text>

                  <Flex style={{ gap: '12px' }} mt={3} mb={4}>
                    <Button disabled={!selectedPatient.athenaId} style={{ flex: 1, borderRadius: '9px', fontSize: '14px', backgroundColor: !selectedPatient.athenaId ? '#d5d5d5' : '#62799c' }} paddingY={'4px'} variant={'primary'} onClick={() => {
                      closeOverlay()
                      navigate(`/patient/${selectedPatient.athenaId}`)
                    }}>Profile</Button>
                    <Button style={{ flex: 1, borderRadius: '9px', border: '1px solid #62799c', fontSize: '14px', backgroundColor: !selectedPatient.phone ? '#d5d5d5' : '' }} paddingY={'4px'} variant={'outline'} onClick={() => {
                      if (!!selectedPatient.phone) {
                        setIsSendSms(true)
                      }
                    }}>SMS</Button>
                  </Flex>

                  <Separator />

                  <Flex flexDirection={'column'} justifyContent={'center'} alignItems={'center'} flex={1}>
                    <Flex mt={4} style={{ width: '75%' }} mb={2} justifySelf={'center'} alignSelf={'center'}>
                      <Button disabled={!selectedPatient.phone} paddingY={'4px'} style={{ borderRadius: '9px', border: '1px solid #62799c', fontSize: '14px', backgroundColor: !selectedPatient.phone ? '#d5d5d5' : '' }} variant={'outline'} onClick={() => {
                        window.open(`dialpad://${selectedPatient.phone.replace(/[^\d+]/g, '')}?launchMinimode=1&crmLogging=0&showDispositions=1&customData=${encodeURI(`${selectedPatient.firstName} ${selectedPatient.lastName}` || '')}`);
                      }}>
                        Call {formatPhoneNumber(selectedPatient.phone)}
                      </Button>
                    </Flex>
                    <Flex style={{ width: '75%' }} mb={2} justifySelf={'center'} alignSelf={'center'}>
                      <Button disabled={!selectedPatient.stripeId} paddingY={'4px'} style={{ borderRadius: '9px', border: '1px solid #62799c', fontSize: '14px', backgroundColor: !selectedPatient.stripeId ? '#d5d5d5' : '' }} variant={'outline'} onClick={() => {
                        window.open(`https://dashboard.stripe.com/${isPreviewMode ? 'test/' : ''}customers/${selectedPatient.stripeId}`, '_blank')?.focus();
                      }}>
                        Stripe
                      </Button>
                    </Flex>
                    <Flex style={{ width: '75%' }} mb={2} justifySelf={'center'} alignSelf={'center'}>
                      <Button disabled={!selectedPatient.athenaId} paddingY={'4px'} style={{ borderRadius: '9px', border: '1px solid #62799c', fontSize: '14px', backgroundColor: !selectedPatient.athenaId ? '#d5d5d5' : '' }} variant={'outline'} onClick={() => {
                        window.open(`${webappRoute}/?vp=${selectedPatient.athenaId}`, '_blank')?.focus();
                      }}>
                        Admin View
                      </Button>
                    </Flex>
                    <Flex style={{ width: '75%' }} mb={2} justifySelf={'center'} alignSelf={'center'}>
                      <Button disabled={!selectedPatient.athenaId} paddingY={'4px'} style={{ borderRadius: '9px', border: '1px solid #62799c', fontSize: '14px', backgroundColor: !selectedPatient.athenaId ? '#d5d5d5' : '' }} variant={'outline'} onClick={() => {
                        toastId.current = toast(<Flex flexDirection={'column'}>
                          <Text fontWeight={400}>Start Impersonation</Text>
                          <Text fontWeight={300}>for patient {selectedPatient.athenaId}</Text>
                        </Flex>, { autoClose: false, isLoading: true, closeButton: false });
                        impersonate()
                      }}>
                        Impersonate Patient
                      </Button>
                    </Flex>
                    <Flex style={{ width: '75%' }} mb={4} justifySelf={'center'} alignSelf={'center'}>
                      <Button disabled={!selectedPatient.athenaId} paddingY={'4px'} style={{ borderRadius: '9px', border: '1px solid #62799c', fontSize: '14px', backgroundColor: !selectedPatient.athenaId ? '#e5e5e5' : '' }} variant={'outline'} onClick={() => {
                        setIsSendReplacementKit(true)
                      }}>
                        Req. Replacement Kit
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              )}
            </Flex>

            <Separator />
          </>
        )}
        <Flex justifyContent={'space-between'}>
          {/* <Flex alignItems={'center'}>
            <Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>Type</Text>
            <Box style={{
              fontWeight: 500, fontSize: '13px',
              background: 'linear-gradient(358deg, #e5e4e4, transparent)',
              border: '1px solid #eee',
              marginLeft: '5px',
              marginRight: '5px',
              borderRadius: '5px',
              opacity: 0.75
            }} paddingX={2} paddingY={1}>
              /
            </Box>
            <Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>to see all other commands</Text>
          </Flex> */}
          <Flex alignItems={'center'}>
            <Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>Press</Text>
            <Box style={{
              fontWeight: 500, fontSize: '13px',
              background: 'linear-gradient(358deg, #e5e4e4, transparent)',
              border: '1px solid #eee',
              marginLeft: '5px',
              marginRight: '5px',
              borderRadius: '5px',
              opacity: 0.75
            }} paddingX={2} paddingY={1}>
              Enter
            </Box>
            <Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>to run search</Text>
          </Flex>
          <Flex alignItems={'center'}>
            <Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>Press</Text>
            <Box style={{
              fontWeight: 500, fontSize: '13px',
              background: 'linear-gradient(358deg, #e5e4e4, transparent)',
              border: '1px solid #eee',
              marginLeft: '5px',
              marginRight: '5px',
              borderRadius: '5px',
              opacity: 0.75
            }} paddingX={2} paddingY={1}>
              Esc
            </Box>
            <Text style={{ fontWeight: 300, fontSize: '13px', opacity: 0.5 }}>to close</Text>
          </Flex>
        </Flex>

        {!!selectedPatient && !!selectedPatient.phone && !!isSendSms && (
          <Modal
            isOpen={!!isSendSms}
            onBackgroundClick={() => setIsSendSms(false)}
            onEscapeKeydown={() => setIsSendSms(false)}>
            <SendSms patientId={selectedPatient.athenaId || ''} defaultText=' ' id={formatPhoneNumber(selectedPatient.phone) || selectedPatient.phone} name={`${selectedPatient.firstName} ${selectedPatient.lastName}`} close={() => { setIsSendSms(false) }} />
          </Modal>
        )}
        {!!selectedPatient && !!selectedPatient.athenaId && !!isSendReplacementKit && (
          <Modal
            isOpen={!!isSendReplacementKit}
            onBackgroundClick={() => setIsSendReplacementKit(false)}
            onEscapeKeydown={() => setIsSendReplacementKit(false)}>
            <Box style={{ margin: '0 auto', maxHeight: '90vh', alignSelf: 'center', borderRadius: '20px', position: 'relative', backgroundColor: 'white', padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <RequestReplacementKit patientId={selectedPatient.athenaId} close={() => setIsSendReplacementKit(false)} />
            </Box>
          </Modal>
        )}
      </Container>
    </Overlay>
  );
};

export default NavigateOverlayBox;
